import { useId, useState, type ReactNode } from 'react';
import { PencilIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { isWorkflowRejection } from '../errors';
import {
  WORKFLOW_NAME_MAX_LENGTH,
  useRenameWorkflow,
} from '../hooks/useRenameWorkflow';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * A workflow's display name, editable in place.
 *
 * One control, used by the list row and the graph header alike (FR-031), so
 * renaming behaves identically wherever it is done. `renderDisplay` lets a
 * caller decide what the name itself is when not being edited — the list makes
 * it the link that opens the workflow, the header leaves it as text — without
 * this component knowing about routing.
 *
 * Committing: Enter, or moving focus away. `Esc` is the one way to discard a
 * draft, and it is explicit. Blur used to discard too, which meant typing a
 * name and clicking anywhere — including the link beside it — silently threw
 * the edit away; a rename is trivially reversible, losing typing is not.
 *
 * A rejected name keeps the field open with the message beside it, and the
 * next keystroke clears that rejection: without it the error outlived the
 * correction and, because blur is suppressed while an error stands, the field
 * could never be left at all.
 */
export function WorkflowName({
  workspaceId,
  workflowId,
  name,
  className,
  renderDisplay,
  isEditing: controlledEditing,
  onEditingChange,
}: {
  workspaceId: string;
  workflowId: string;
  name: string;
  className?: string;
  renderDisplay?: (name: string) => ReactNode;
  /**
   * Supply this to drive editing from outside — the list starts a rename from
   * its row menu, so the name itself carries no second control. Left out, the
   * component owns the state and renders its own pencil, which is what the
   * graph header (with no menu of its own) needs.
   */
  isEditing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}) {
  const isControlled = controlledEditing !== undefined;
  const [uncontrolledEditing, setUncontrolledEditing] = useState(false);
  const isEditing = isControlled ? controlledEditing : uncontrolledEditing;
  const canWork = useCanWork();
  const setIsEditing = (editing: boolean) => {
    if (!isControlled) setUncontrolledEditing(editing);
    onEditingChange?.(editing);
  };
  const [draft, setDraft] = useState(name);
  // Editing can now begin from outside this component, so seeding the field
  // cannot live in the pencil's click handler. Adjusting state during render on
  // a changed input is React's documented alternative to an effect.
  const [wasEditing, setWasEditing] = useState(isEditing);
  if (isEditing !== wasEditing) {
    setWasEditing(isEditing);
    if (isEditing) setDraft(name);
  }
  const [localError, setLocalError] = useState<string | null>(null);
  const rename = useRenameWorkflow(workspaceId);
  const errorId = useId();

  const stopEditing = () => {
    setIsEditing(false);
    setLocalError(null);
    rename.reset();
  };

  const validate = (candidate: string): string | null => {
    if (candidate.length === 0) return 'The name must not be empty.';
    if (candidate.length > WORKFLOW_NAME_MAX_LENGTH) {
      return `The name must be at most ${WORKFLOW_NAME_MAX_LENGTH} characters.`;
    }
    return null;
  };

  const commit = () => {
    if (rename.isPending) return;
    const trimmed = draft.trim();
    if (trimmed === name) {
      stopEditing();
      return;
    }
    const invalid = validate(trimmed);
    if (invalid !== null) {
      setLocalError(invalid);
      return;
    }
    setLocalError(null);
    rename.mutate({ workflowId, name: trimmed }, { onSuccess: stopEditing });
  };

  if (!isEditing) {
    return (
      <span className={cn('flex items-center gap-1.5', className)}>
        {renderDisplay ? renderDisplay(name) : <span>{name}</span>}
        {/* Only the uncontrolled form carries a pencil. Where a caller drives
            editing it already has somewhere to offer the action from, and a
            second control beside the name would be the same action twice. */}
        {!isControlled && canWork && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Rename ${name}`}
            onClick={() => {
              setDraft(name);
              setIsEditing(true);
            }}
          >
            <PencilIcon />
          </Button>
        )}
      </span>
    );
  }

  const rejection = isWorkflowRejection(rename.error)
    ? (rename.error.violations[0]?.message ?? rename.error.message)
    : rename.error !== null
      ? rename.error.message
      : null;
  const error = localError ?? rejection;

  return (
    <span className={cn('flex flex-col gap-1', className)}>
      <Input
        autoFocus
        aria-label="Workflow name"
        aria-invalid={error !== null}
        aria-describedby={error !== null ? errorId : undefined}
        value={draft}
        disabled={rename.isPending}
        onChange={(event) => {
          setDraft(event.target.value);
          setLocalError(null);
          // The rejection described the previous attempt; keeping it would
          // outlive its subject and, since blur is suppressed while an error
          // stands, would trap the field open.
          rename.reset();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            // The graph page also closes its step panel on Esc, and one key
            // press should do one thing.
            event.stopPropagation();
            stopEditing();
          }
        }}
        onBlur={() => {
          if (rename.isPending) return;
          // A name the field has already refused stays open to be fixed;
          // anything else commits, so leaving the field never loses typing.
          if (validate(draft.trim()) !== null) {
            setLocalError(validate(draft.trim()));
            return;
          }
          commit();
        }}
      />
      {error !== null && (
        <span id={errorId} role="alert" className="text-destructive text-xs">
          {error}
        </span>
      )}
    </span>
  );
}
